import { ViewRoom } from '@xeetr/types/models'
import { Copy, Ellipsis, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import FloatingLabel from '../../inputs/FloatingLabel'
import LabeledInputNumber from '../../inputs/LabeledInputNumber'

import TableContent from './tables/TableContent'
import TableHeader from './tables/TableHeader'

import LabeledInputSwitch from '@/components/inputs/LabeledInputSwitch'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

interface Props {
  onCopy: () => void
  onDelete: () => void
}

const RoomContent = ({ onCopy, onDelete }: Props) => {
  const { control, setValue, watch } = useFormContext<ViewRoom>()
  const tables = useWatch({ control, name: 'tables' })
  const useAutoCalculate = useWatch({ control, name: 'settings.useAutoCalculateRoomCapacity' })
  const room = watch()

  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (useAutoCalculate) {
      const totalCapacity =
        tables?.reduce((sum, table) => sum + (table.maxCapacity || table.size), 0) || 0
      setValue('settings.capacity', totalCapacity)
    }
  }, [useAutoCalculate, tables, setValue])

  return (
    <div className="relative space-y-8">
      {/* Actions */}
      <div className="absolute right-0 top-0">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon">
              <Ellipsis className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56" align="end" side="bottom" sideOffset={5}>
            <div className="space-y-1">
              <div className="px-2 py-1.5 text-sm font-semibold">Optionen</div>
              <Separator />
              <Controller
                name="settings.default"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center justify-between rounded-sm px-2 py-2 hover:bg-gray-100">
                    <span className="text-sm">Standard</span>
                    <Switch checked={Boolean(field.value)} onCheckedChange={field.onChange} />
                  </div>
                )}
              />
              <Controller
                name="settings.onlineBookable"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center justify-between rounded-sm px-2 py-2 hover:bg-gray-100">
                    <span className="text-sm">Online Buchbar</span>
                    <Switch checked={Boolean(field.value)} onCheckedChange={field.onChange} />
                  </div>
                )}
              />
              <Separator />
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-gray-100"
                onClick={() => onCopy()}
              >
                <Copy className="h-4 w-4" />
                Duplizieren
              </button>
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm text-red-600 hover:bg-red-50"
                onClick={() => onDelete()}
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Basic Information */}
      <section>
        <h3 className="mb-1 text-base font-semibold text-gray-900">Grundinformationen</h3>
        <p className="mb-4 text-sm text-gray-600">Name und Kapazität des Raumes</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <FloatingLabel label="Name">
                <Input {...field} value={field.value || ''} />
              </FloatingLabel>
            )}
          />
          <div className="grid grid-cols-2 gap-2 space-y-2">
            <Controller
              name="settings.capacity"
              control={control}
              render={({ field }) => (
                <LabeledInputNumber
                  label="Kapazität"
                  inputId={field.name}
                  inputRef={field.ref}
                  value={field.value as number}
                  min={1}
                  onChange={field.onChange}
                  disabled={useAutoCalculate}
                />
              )}
            />
            <Controller
              name="settings.useAutoCalculateRoomCapacity"
              control={control}
              render={({ field }) => (
                <LabeledInputSwitch
                  label="Automatische Kapazitätsberechnung"
                  checked={Boolean(field.value)}
                  onValueChange={field.onChange}
                />
              )}
            />
          </div>
        </div>
      </section>

      <section>
        <h3 className="mb-1 text-base font-semibold text-gray-900">Notizen</h3>
        <Controller
          control={control}
          name="settings.note"
          render={({ field }) => (
            <FloatingLabel label="Interne Notizen zu diesem Raum">
              <Textarea {...field} value={field.value || ''} rows={5} />
            </FloatingLabel>
          )}
        />
      </section>

      <section>
        <h3 className="mb-1 text-base font-semibold text-gray-900">Tischverwaltung</h3>
        <p className="mb-4 text-sm text-gray-600">
          Verwalten Sie die Tische in diesem Raum für eine bessere Platzoptimierung
        </p>
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
          <CollapsibleTrigger asChild>
            <div>
              <TableHeader
                collapsed={!isOpen}
                tables={room.tables ?? []}
                toggleCollapse={() => setIsOpen(!isOpen)}
              />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <TableContent />
          </CollapsibleContent>
        </Collapsible>
      </section>
    </div>
  )
}

export default RoomContent
